import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const buildPageItems = (page, totalPages) => {
  if (totalPages <= 1) return [];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => ({ type: "page", value: i + 1 }));
  }

  const items = [{ type: "page", value: 1 }];
  const windowStart = Math.max(2, page - 1);
  const windowEnd = Math.min(totalPages - 1, page + 1);

  if (windowStart > 2) items.push({ type: "ellipsis", key: "start" });
  for (let p = windowStart; p <= windowEnd; p += 1) {
    items.push({ type: "page", value: p });
  }
  if (windowEnd < totalPages - 1) items.push({ type: "ellipsis", key: "end" });
  items.push({ type: "page", value: totalPages });
  return items;
};

const CdrTablePagination = ({
  isDark,
  page,
  pageSize,
  total,
  totalPages,
  loading = false,
  onPageChange,
  className,
}) => {
  const [jumpPage, setJumpPage] = useState(String(page));
  const mutedClass = isDark ? "text-slate-400" : "text-gray-500";
  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-gray-300";

  useEffect(() => {
    setJumpPage(String(page));
  }, [page]);

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * pageSize, total);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);

  const handlePageJump = () => {
    const target = parseInt(jumpPage, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(`Geçerli bir sayfa girin (1–${totalPages})`);
      setJumpPage(String(page));
      return;
    }
    if (target !== page) onPageChange(target);
  };

  if (total === 0 && !loading) return null;

  return (
    <div
      className={cn(
        "px-4 sm:px-5 py-3 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3",
        isDark ? "border-slate-800 bg-slate-900/40" : "border-gray-100 bg-gray-50/80",
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
        <div className={`inline-flex items-center gap-1.5 text-xs sm:text-sm ${mutedClass}`}>
          <span className={cn("font-semibold tabular-nums", isDark ? "text-slate-100" : "text-gray-900")}>
            {total.toLocaleString("tr-TR")}
          </span>
          <span>kayıt</span>
        </div>
        {total > 0 && (
          <>
            <span className={`hidden sm:inline w-px h-4 ${isDark ? "bg-slate-700" : "bg-gray-200"}`} />
            <span className={`text-xs sm:text-sm tabular-nums ${mutedClass}`}>
              <span className={isDark ? "text-slate-200" : "text-gray-800"}>{rangeStart}–{rangeEnd}</span>
              {" arası gösteriliyor"}
            </span>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
        {totalPages > 1 && (
          <>
            <div className="inline-flex items-center gap-0.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => onPageChange(1)}
                className={cn("h-8 w-8 p-0", isDark && "border-slate-700 hover:bg-slate-800")}
                title="İlk sayfa"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => onPageChange(page - 1)}
                className={cn("h-8 w-8 p-0", isDark && "border-slate-700 hover:bg-slate-800")}
                title="Önceki"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              <div className="inline-flex items-center gap-0.5 mx-0.5">
                {pageItems.map((item) =>
                  item.type === "ellipsis" ? (
                    <span key={item.key} className={`px-1 text-xs ${mutedClass}`}>…</span>
                  ) : (
                    <Button
                      key={item.value}
                      type="button"
                      variant={item.value === page ? "default" : "outline"}
                      size="sm"
                      disabled={loading}
                      onClick={() => onPageChange(item.value)}
                      className={cn(
                        "h-8 min-w-8 px-2 text-xs tabular-nums",
                        item.value === page
                          ? (isDark ? "bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-500" : "bg-indigo-600 hover:bg-indigo-700 text-white")
                          : (isDark ? "border-slate-700 hover:bg-slate-800" : "")
                      )}
                    >
                      {item.value}
                    </Button>
                  )
                )}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => onPageChange(page + 1)}
                className={cn("h-8 w-8 p-0", isDark && "border-slate-700 hover:bg-slate-800")}
                title="Sonraki"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => onPageChange(totalPages)}
                className={cn("h-8 w-8 p-0", isDark && "border-slate-700 hover:bg-slate-800")}
                title="Son sayfa"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>

            <div className={`hidden md:block w-px h-6 ${isDark ? "bg-slate-700/80" : "bg-gray-200"}`} />

            <div className="inline-flex items-center gap-1.5">
              <span className={`text-xs whitespace-nowrap ${mutedClass}`}>Sayfaya git</span>
              <Input
                type="number"
                min={1}
                max={totalPages}
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handlePageJump(); }}
                className={cn("h-8 w-14 px-2 text-xs text-center tabular-nums", inputClass)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={loading}
                onClick={handlePageJump}
                className={cn("h-8 px-2.5 text-xs", isDark && "border-slate-700 hover:bg-slate-800")}
              >
                Git
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CdrTablePagination;
